import { useMemo } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { useTour } from '../../../tours/useTour';
import type { ScholarshipDetailContext } from './ScholarshipDetailLayout';
import { LineupCard } from './LineupCard';
import { knownSlots, LINEUP_SIDES, lineupKey, normalizeSlots } from './lineups';
import { RosterLegend } from './RosterLegend';
import { rosterSettingsOf } from './rosterPalette';
import { isRecruitingBoard } from './boardPurpose';
import { useLineups } from './useLineups';
import { useRosterPlayers } from './useRosterPlayers';
import { useRosterSport } from './useRosterSport';
import './rosterManagement.css';

/**
 * Two batting orders, against a left-hander and against a right-hander. Every change saves that
 * side (a full replace of the one lineup), optimistically: the rows move first, and move back if
 * the save is refused. Saves are one at a time per side; see useLineups.
 */
export const ScholarshipLineupsTab = () => {
  const { scholarship } = useOutletContext<ScholarshipDetailContext>();
  // A recruiting board has no Lineups tab. An old link or bookmark lands on Players instead.
  if (isRecruitingBoard(scholarship)) return <Navigate to="../players" replace />;
  return <LineupsView />;
};

const LineupsView = () => {
  const { scholarship } = useOutletContext<ScholarshipDetailContext>();
  const sport = useRosterSport();
  const settings = rosterSettingsOf(scholarship);
  const {
    players,
    error: playersError,
    refresh: refreshPlayers,
  } = useRosterPlayers(scholarship.id);
  // A 404 names a player who has left the board: the players are stale too, not just the lineup.
  const { lineups, error, change } = useLineups(scholarship.id, refreshPlayers);

  const playersById = useMemo(
    () => new Map((players ?? []).map((p) => [p.id, p])),
    [players],
  );

  const loaded = players != null && lineups != null;
  useTour('rosters', {
    ready: loaded || error != null || playersError != null,
    hasData: loaded && players.length > 0,
    pageTourKey: 'rosters.lineups',
    dataTourKey: null,
  });

  const shownError = error ?? playersError;

  if (!loaded) {
    return shownError ? (
      <div className="roster-view__error" role="alert">
        {shownError}
      </div>
    ) : (
      <div>Loading lineups…</div>
    );
  }

  return (
    <div className="roster-view lineups-tab">
      {shownError && (
        <div className="roster-view__error" role="alert">
          {shownError}
        </div>
      )}
      <div className="lineups-tab__cards">
        {LINEUP_SIDES.map((side, index) => (
          <LineupCard
            key={side}
            side={side}
            slots={normalizeSlots(knownSlots(lineups[lineupKey(side)], playersById))}
            players={players}
            playersById={playersById}
            settings={settings}
            sport={sport}
            onChange={change}
            ownsRowAnchors={index === 0}
          />
        ))}
      </div>
      <RosterLegend settings={settings} sections={['handedness', 'traits']} sides={['left', 'right']} />
    </div>
  );
};
